import React from "react";
import { ArrowUpRight } from "lucide-react";
import { postMortems, postMortemMeta } from "../data/mock";

function PaperPlaneCrash() {
  // Custom illustrated SVG — paper plane nose-diving with motion lines & crash puff
  return (
    <svg
      viewBox="0 0 220 140"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full"
      aria-hidden="true"
    >
      {/* dotted trail */}
      <path
        d="M10 20 Q 60 10, 90 30 T 150 60"
        fill="none"
        stroke="#0F0F10"
        strokeWidth="2"
        strokeDasharray="2 6"
        strokeLinecap="round"
        opacity="0.55"
      />

      {/* motion swoosh lines */}
      <g stroke="#0F0F10" strokeWidth="2" strokeLinecap="round" opacity="0.45">
        <line x1="118" y1="48" x2="132" y2="56" />
        <line x1="108" y1="58" x2="122" y2="66" />
        <line x1="100" y1="70" x2="114" y2="78" />
      </g>

      {/* paper plane body — tilted nose-down */}
      <g transform="translate(150 65) rotate(48)">
        {/* outer triangle */}
        <path
          d="M 0 -34 L 46 18 L 0 8 L -46 18 Z"
          fill="#FFE357"
          stroke="#0F0F10"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        {/* center fold (darker) */}
        <path
          d="M 0 -34 L 0 8 L -46 18 Z"
          fill="#F5C518"
          stroke="#0F0F10"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        {/* fold crease line */}
        <line
          x1="0"
          y1="-34"
          x2="0"
          y2="8"
          stroke="#0F0F10"
          strokeWidth="2"
        />
      </g>

      {/* crash puff (bottom right) */}
      <g transform="translate(178 110)">
        <circle cx="0" cy="0" r="14" fill="#FFF8E7" stroke="#0F0F10" strokeWidth="2.2" />
        <circle cx="-14" cy="-3" r="9" fill="#FFF8E7" stroke="#0F0F10" strokeWidth="2.2" />
        <circle cx="12" cy="-6" r="7" fill="#FFF8E7" stroke="#0F0F10" strokeWidth="2.2" />
        <circle cx="-22" cy="6" r="5" fill="#FFF8E7" stroke="#0F0F10" strokeWidth="2.2" />
        {/* impact stars */}
        <g stroke="#B91C1C" strokeWidth="2.2" strokeLinecap="round">
          <line x1="-4" y1="-22" x2="-4" y2="-30" />
          <line x1="6" y1="-20" x2="11" y2="-26" />
          <line x1="-12" y1="-20" x2="-17" y2="-26" />
        </g>
      </g>

      {/* small "X" mark above crash — sad eye style */}
      <g
        transform="translate(160 78)"
        stroke="#B91C1C"
        strokeWidth="2.5"
        strokeLinecap="round"
      >
        <line x1="-4" y1="-4" x2="4" y2="4" />
        <line x1="4" y1="-4" x2="-4" y2="4" />
      </g>
    </svg>
  );
}

export default function PostMortemSection() {
  return (
    <section
      id="postmortem"
      className="py-24 px-4 sm:px-6 lg:px-10 bg-[#F4E3BF]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0F0F10] text-[#FFE357] text-xs font-semibold uppercase tracking-widest mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FFE357]"></span>
              Post Mortem
            </div>
            <h2 className="text-5xl sm:text-6xl font-extrabold tracking-tight">
              When things <span className="squiggle">don't fly</span>
            </h2>
            <p className="mt-4 text-lg text-[#0F0F10]/75 leading-relaxed">
              {postMortemMeta.subtitle}
            </p>
          </div>
          <div className="font-mono text-sm text-[#0F0F10]/60">lessons × 2</div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {postMortems.map((p) => (
            <article key={p.id} className="card-soft p-6 sm:p-8 flex flex-col">
              <div className="relative aspect-[16/9] rounded-2xl overflow-hidden border-[1.5px] border-[#0F0F10] bg-[#FFF1CB]">
                {p.image ? (
                  <img
                    src={p.image}
                    alt={p.title}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <>
                    {/* faint grid */}
                    <div
                      className="absolute inset-0 opacity-[0.06]"
                      style={{
                        backgroundImage:
                          "linear-gradient(#0F0F10 1px, transparent 1px), linear-gradient(90deg, #0F0F10 1px, transparent 1px)",
                        backgroundSize: "20px 20px"
                      }}
                    ></div>
                    <div className="absolute inset-0 p-4">
                      <PaperPlaneCrash />
                    </div>
                  </>
                )}
                <div className="absolute top-3 left-3 font-mono text-xs px-2 py-1 rounded-md bg-[#FFF8E7] border border-[#0F0F10]/40">
                  PM-0{p.id}
                </div>
                <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0F0F10] text-[#FFE357] text-[11px] font-bold uppercase tracking-widest">
                  {p.tag}
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <span className="tag-pill bg-[#FECACA] border-[#7F1D1D] text-[#7F1D1D]">
                  {p.reason}
                </span>
                {(p.tags || []).map((t) => (
                  <span key={t} className="tag-pill">
                    {t}
                  </span>
                ))}
              </div>

              <h3 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight">
                {p.title}
              </h3>
              <p className="mt-3 text-[15px] text-[#0F0F10]/75 leading-relaxed">
                {p.description}
              </p>

              <div className="mt-6 flex items-center justify-between">
                {p.url ? (
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-yellow text-sm py-2.5 px-6"
                  >
                    Read why
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                ) : (
                  <button className="btn-yellow text-sm py-2.5 px-6">
                    Read why
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                )}
                <div className="font-mono text-xs text-[#0F0F10]/50">
                  lesson-{p.id}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
